import { TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import localAxios from '../localAxios';
import { Subscription } from '../subscription';
import { allTeamsURL } from './urls';

type TeamSubscription = Subscription & {
  isPurchaser: boolean;
};

type TeamPermissions = {
  canOpenStripePortal: boolean;
  canOpenStripeCheckout: boolean;
  canViewPlans: boolean;
  canChangeCanInvite: boolean;
  canChangeCanViewEmail: boolean;
  canInvite: boolean;
};

type Owner = {
  publicId: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type Team = {
  id: string;
  name: string;
  subscriptionId: number | null;
  subscription: TeamSubscription | null;
  permissions: TeamPermissions;
  role: TeamRole;
  createdDate: string;
  updatedDate: string;
  owner: Owner;
  settings?: TeamSettings;
};

export type TeamSettings = {
  canInvite: Array<TeamRole>;
  canViewEmail: Array<TeamRole>;
};

export type TeamSettingNames = Record<
  keyof TeamSettings,
  Array<keyof typeof TeamRole>
>;

export type GetTeamsResponse = Array<Team>;

export type TeamRoleName = keyof typeof TeamRole;

function mapTeamRoleToTeamRoleName(role: TeamRole): keyof typeof TeamRole {
  return TeamRole[role] as keyof typeof TeamRole;
}

export function teamSettingsToTeamSettingNames(
  settings: TeamSettings
): TeamSettingNames {
  return {
    canInvite: settings.canInvite.map(mapTeamRoleToTeamRoleName),
    canViewEmail: settings.canViewEmail.map(mapTeamRoleToTeamRoleName),
  };
}

export async function getTeams() {
  const url = allTeamsURL();
  const { data } = await localAxios.get<GetTeamsResponse>(url.href);

  return data.map(({ name, ...team }) => {
    return {
      ...team,
      displayName:
        name ||
        `${team.owner.firstName} ${team.owner.lastName} (${translate('Team')})`,
    };
  });
}

export type TeamWithDisplayName = Awaited<ReturnType<typeof getTeams>>[number];
